import { stripHTML } from 'utils/helper';

const COMMENTARY_ITEM_ID = 'commentary';

const getOriginalFeedbackContext = (submission) => ({
    criteria: Object.fromEntries(
        Object.entries(submission?.ai?.grading ?? {})
            .filter(([, grading]) => grading?.optionId)
            .map(([criterionId, grading]) => [criterionId, grading.optionId])
    ),
    grade_overrides: {},
    grading_explanations: {},
});

const isCriterionDifferentFromSuggestion = ({ criterionId, selectedOptionId, submission }) => {
    if (!selectedOptionId) return false;
    const suggestedOptionId = submission?.ai?.grading?.[criterionId]?.optionId;
    return Boolean(suggestedOptionId) && selectedOptionId !== suggestedOptionId;
};

const hasExplanation = (value) => typeof value === 'string' && Boolean(stripHTML(value).trim());

const normalizeExplanation = (value) => (typeof value === 'string' ? stripHTML(value).trim() : '');

const getOptionPoints = (criterion, optionId) => {
    const points = Number(criterion.options?.find(({ id }) => id === optionId)?.rate);
    return Number.isFinite(points) ? points : null;
};

const hasOverride = (value) => value !== undefined && value !== null && value !== '';

const getEffectivePoints = (criterion, optionId, override) => {
    if (hasOverride(override)) {
        const overridePoints = Number(override);
        if (Number.isFinite(overridePoints)) return overridePoints;
    }
    return getOptionPoints(criterion, optionId);
};

const getCriterionRegenerationReasons = ({ criterion, submission, form, gradeOverrides, baselineContext }) => {
    const reasons = [];
    const originalContext = getOriginalFeedbackContext(submission);
    const selectedOptionId = form?.criteria?.[criterion.id];
    const baselineOptionId = baselineContext?.criteria?.[criterion.id] ?? originalContext.criteria[criterion.id];
    const override = gradeOverrides?.[criterion.id];
    const baselineOverride = baselineContext?.grade_overrides?.[criterion.id];

    if (selectedOptionId && baselineOptionId && selectedOptionId !== baselineOptionId) {
        reasons.push('grade');
    } else if (hasOverride(override) || hasOverride(baselineOverride)) {
        const points = getEffectivePoints(criterion, selectedOptionId, override);
        const baselinePoints = getEffectivePoints(criterion, baselineOptionId, baselineOverride);
        if (points !== baselinePoints) reasons.push('grade');
    }

    const explanation = normalizeExplanation(form?.grading_explanations?.[criterion.id]);
    const baselineExplanation = normalizeExplanation(baselineContext?.grading_explanations?.[criterion.id]);
    if (explanation !== baselineExplanation) reasons.push('explanation');

    return reasons;
};

const getFeedbackRegenerationReasons = ({ assignment, submission, form, gradeOverrides }) => {
    return (assignment?.rubric?.criteria ?? []).reduce((reasons, criterion) => {
        const criterionReasons = [];
        const selectedOptionId = form?.criteria?.[criterion.id];
        const suggestedOptionId = submission?.ai?.grading?.[criterion.id]?.optionId;
        const suggestedOption = criterion.options?.find(({ id }) => id === suggestedOptionId);
        const override = gradeOverrides?.[criterion.id];

        if (isCriterionDifferentFromSuggestion({ criterionId: criterion.id, selectedOptionId, submission })) {
            criterionReasons.push('grade');
        } else if (
            override !== undefined &&
            override !== '' &&
            suggestedOption &&
            Number(override) !== Number(suggestedOption.rate)
        ) {
            criterionReasons.push('grade');
        }
        if (hasExplanation(form?.grading_explanations?.[criterion.id])) criterionReasons.push('explanation');

        if (criterionReasons.length) reasons[criterion.id] = criterionReasons;
        return reasons;
    }, {});
};

const getRegenerableFeedbackItemIds = (feedback, criterionIds) => {
    const normalizedCriterionIds = criterionIds === undefined ? null : new Set(criterionIds);
    if (normalizedCriterionIds?.size === 0) return [];
    if (typeof feedback === 'string') return [COMMENTARY_ITEM_ID];
    if (!feedback || typeof feedback !== 'object') return [];

    const suggestions = feedback.improvement_suggestions ?? [];
    const isRubricFeedback = feedback.feedback_type === 'rubric';
    const detailedItemIds = suggestions
        .filter(({ criterion_id: criterionId }) => {
            if (!normalizedCriterionIds) return true;
            return !isRubricFeedback || normalizedCriterionIds.has(criterionId);
        })
        .map(({ id }) => id)
        .filter(Boolean);

    return [
        ...(!isRubricFeedback && Object.prototype.hasOwnProperty.call(feedback, 'commentary')
            ? [COMMENTARY_ITEM_ID]
            : []),
        ...detailedItemIds,
    ];
};

const getLatestFeedbackItemHistoryEntry = (feedbackHistory = [], itemId) => {
    return [...feedbackHistory].reverse().find(({ item_ids: itemIds }) => (itemIds ?? []).includes(itemId)) ?? null;
};

const getFeedbackItemCriterionIds = (assignment, feedback, itemId) => {
    if (feedback?.feedback_type === 'rubric') {
        const criterionId = feedback.improvement_suggestions?.find(({ id }) => id === itemId)?.criterion_id;
        return criterionId ? [criterionId] : [];
    }
    return (assignment?.rubric?.criteria ?? []).map(({ id }) => id);
};

const getFeedbackRegenerationState = ({ assignment, submission, form, gradeOverrides }) => {
    const feedback = form?.feedback;
    const feedbackHistory = submission?.feedback_history ?? [];
    const originalContext = getOriginalFeedbackContext(submission);
    const criteriaById = new Map((assignment?.rubric?.criteria ?? []).map((criterion) => [criterion.id, criterion]));
    const reasons = {};
    const neededItemIds = [];
    const updatedItemIds = [];

    getRegenerableFeedbackItemIds(feedback).forEach((itemId) => {
        const latestHistoryEntry = getLatestFeedbackItemHistoryEntry(feedbackHistory, itemId);
        // An explicit revert suppresses suggestions until this item is generated again.
        if (latestHistoryEntry?.action === 'revert') return;

        const isCompletedRegeneration = latestHistoryEntry?.action === 'regeneration';
        const baselineContext = latestHistoryEntry?.context ?? originalContext;
        let itemNeedsRegeneration = false;

        getFeedbackItemCriterionIds(assignment, feedback, itemId).forEach((criterionId) => {
            const criterion = criteriaById.get(criterionId);
            if (!criterion) return;
            const criterionReasons = getCriterionRegenerationReasons({
                criterion,
                submission,
                form,
                gradeOverrides,
                baselineContext,
            });
            if (!criterionReasons.length) return;

            itemNeedsRegeneration = true;
            reasons[criterionId] = [...new Set([...(reasons[criterionId] ?? []), ...criterionReasons])];
        });

        if (itemNeedsRegeneration) {
            neededItemIds.push(itemId);
        } else if (isCompletedRegeneration) {
            updatedItemIds.push(itemId);
        }
    });

    return {
        feedbackRegenerationReasons: reasons,
        feedbackRegenerationNeededItemIds: neededItemIds,
        feedbackUpdatedItemIds: updatedItemIds,
    };
};

const getPendingFeedbackItemIds = (activeBatches = []) => {
    return [...new Set(activeBatches.flatMap(({ item_ids: itemIds }) => itemIds ?? []))];
};

const getLatestRevertibleHistoryEntry = (feedbackHistory = [], itemIds = []) => {
    const latestEntry = [...feedbackHistory]
        .reverse()
        .find((entry) => !itemIds.length || itemIds.some((itemId) => (entry.item_ids ?? []).includes(itemId)));

    // Only the latest generation can be reverted; never fall back past a revert.
    if (
        latestEntry?.action !== 'regeneration' ||
        !latestEntry.previous_feedback ||
        itemIds.some((itemId) => !(latestEntry.item_ids ?? []).includes(itemId))
    ) {
        return null;
    }

    return latestEntry;
};

const getFeedbackItemFragment = (feedback, itemId) => {
    if (itemId === COMMENTARY_ITEM_ID) {
        if (typeof feedback === 'string') return feedback;
        return feedback?.commentary;
    }

    return feedback?.improvement_suggestions?.find(({ id }) => id === itemId);
};

// Merge only items changed by new history events, preserving unrelated local edits.
const mergeFeedbackHistoryUpdates = (feedback, updatedFeedback, feedbackHistory = [], syncedHistoryId = null) => {
    const syncedIndex = feedbackHistory.findIndex(({ id }) => id === syncedHistoryId);
    if (syncedHistoryId && syncedIndex === -1) return feedback;
    const updatedItemIds = new Set(
        feedbackHistory
            .slice(syncedIndex + 1)
            .filter(({ action }) => action === 'regeneration' || action === 'revert')
            .flatMap(({ item_ids: itemIds }) => itemIds ?? [])
    );
    if (!updatedItemIds.size || feedback === null || feedback === undefined) return feedback;

    const commentary = getFeedbackItemFragment(updatedFeedback, COMMENTARY_ITEM_ID);
    const hasUpdatedCommentary = updatedItemIds.has(COMMENTARY_ITEM_ID) && commentary !== undefined;
    if (typeof feedback === 'string') return hasUpdatedCommentary ? commentary : feedback;

    return {
        ...feedback,
        ...(hasUpdatedCommentary && { commentary }),
        ...(feedback.improvement_suggestions && {
            improvement_suggestions: feedback.improvement_suggestions.map((item) => {
                if (!updatedItemIds.has(item.id)) return item;
                return { ...item, ...getFeedbackItemFragment(updatedFeedback, item.id) };
            }),
        }),
    };
};

const getFeedbackRevertPreview = (feedbackHistory = [], itemId) => {
    const historyEntry = getLatestRevertibleHistoryEntry(feedbackHistory, [itemId]);
    if (!historyEntry) return null;

    const previousFeedback = getFeedbackItemFragment(historyEntry.previous_feedback, itemId);
    if (previousFeedback === undefined) return null;

    const historyEntryIndex = feedbackHistory.findIndex(({ id }) => id === historyEntry.id);
    const previousVersionEntry = feedbackHistory
        .slice(0, historyEntryIndex)
        .reverse()
        .find(
            ({ feedback, item_ids: itemIds }) =>
                (itemIds ?? []).includes(itemId) && getFeedbackItemFragment(feedback, itemId) !== undefined
        );

    return {
        feedback: previousFeedback,
        generatedOn: previousVersionEntry?.created_on ?? historyEntry.created_on,
        historyId: historyEntry.id,
        itemId,
    };
};

const getDetailedFeedbackRevertPreview = (feedbackHistory = [], detailedFeedback = []) => {
    const detailedItemIds = new Set(detailedFeedback.map(({ id }) => id));
    const historyEntry = [...feedbackHistory]
        .reverse()
        .find(({ item_ids: itemIds }) => (itemIds ?? []).some((itemId) => detailedItemIds.has(itemId)));
    if (historyEntry?.action !== 'regeneration' || !historyEntry.previous_feedback) return null;

    // Restore the section before its latest regeneration, leaving unchanged items intact.
    // Never offer an older generation after the section has been reverted.
    const itemIds = historyEntry.item_ids.filter((itemId) => detailedItemIds.has(itemId));
    const previousItems = itemIds.map((itemId) => getFeedbackItemFragment(historyEntry.previous_feedback, itemId));
    if (previousItems.some((item) => item === undefined)) return null;

    const previousItemById = new Map(previousItems.map((item) => [item.id, item]));
    const historyEntryIndex = feedbackHistory.findIndex(({ id }) => id === historyEntry.id);
    const previousVersionEntry = feedbackHistory
        .slice(0, historyEntryIndex)
        .reverse()
        .find(({ item_ids: previousItemIds }) => (previousItemIds ?? []).some((itemId) => detailedItemIds.has(itemId)));

    return {
        feedback: detailedFeedback.map((item) => previousItemById.get(item.id) ?? item),
        generatedOn: previousVersionEntry?.created_on ?? historyEntry.created_on,
        historyId: historyEntry.id,
        itemIds,
    };
};

export {
    COMMENTARY_ITEM_ID,
    getDetailedFeedbackRevertPreview,
    getFeedbackRevertPreview,
    getFeedbackRegenerationState,
    getFeedbackRegenerationReasons,
    getLatestRevertibleHistoryEntry,
    getPendingFeedbackItemIds,
    getRegenerableFeedbackItemIds,
    isCriterionDifferentFromSuggestion,
    mergeFeedbackHistoryUpdates,
};
